import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { TunnelService as CloudTunnel } from '@mitel/cloudlink-sdk';
import { PutMaintenanceRequest } from '@mitel/cloudlink-sdk/tunnel';
import { shareReplay, tap } from 'rxjs/operators';
import { MiAccessService } from './miaccess.service';
import { GatewayStatusService } from './gateway-status.service';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { IntegCustomerAdminPortalService } from './integ-customer-admin-portal.service';
import { Util } from '../utils/Util';

@Injectable({
	providedIn: 'root'
})

// A service to handle the integration with the GWP
export class GWPIntegrationService {
	private readonly cloudTunnel: CloudTunnel;
	// components that want to know if the GWP wizard is in progress (aka iframe is active), then 
	// they should register for this change

	public gwpDoneChildMessage: Subject<boolean> = new Subject<boolean>();
	public gwpInprogessStateChange:Subject<any> = new Subject<any>();
	private gwpInprogessState : boolean = false;
	gwp: Subject<boolean> = new Subject<boolean>();
	gwpAccessFlagChanged: Observable<boolean> = this.gwp.pipe(tap(() => console.log('gwp flag changed')), shareReplay(1));
	gwpAccess: boolean = false;

	// tunnel parameters
	tunnel: string = undefined;
	username: string = undefined;
	password: string = undefined;


	//GWPEmails
	emailsAllowed: boolean = false;
	emailApplicationName: string = null;
	emailApplicationNameLong: string = null;

	constructor( 
		private miaccessService: MiAccessService,
		private gatewayStatusService: GatewayStatusService,
		private mivbAccountSettingsService: MiVBAccountSettingsService,
		private capService: IntegCustomerAdminPortalService
	) {
		this.cloudTunnel = new CloudTunnel();
	}

	// extracts the tunnel parameters from the url string if they exist
	extractTunnelParameters( url: string )
	{
		if ( url && url.length > 0 ) 
		{
		  if ( url.indexOf('?') > 0 ) 
		  {
			//there are query parameters
			let theParams = url.split('?')[1];
			if ( theParams != null && theParams.length > 0 ) {
			  this.tunnel = theParams.indexOf('tunnel=') >= 0 ? theParams.split('tunnel=')[1].split('&')[0] : undefined;
			  if ( this.tunnel )
			  {
				this.username = theParams.indexOf('username=') >= 0 ? theParams.split('username=')[1].split('&')[0] : undefined;
				this.password = theParams.indexOf('password=') >= 0 ? theParams.split('password=')[1].split('&')[0] : undefined;
				localStorage.setItem('tunnel', this.tunnel);
				localStorage.setItem('username', this.username);
				localStorage.setItem('password', this.password);
			  }
			}
		  }
		  if(this.tunnel == undefined){
			this.checkStorageForTunnelParameters();
		  }
		}
		// this.tunnel = 'http://temptunnel.ngrok.io';
		// this.username = 'tempuser';
		// this.password = 'temppassword123';
		console.log("Tunnel parameters are ", this.tunnel, this.username, this.password, url);
	}

	// To determine if there are tunnel paramters to use for a new appliance integration
	// use this method
	hasTunnelParameters()
	{
		if ( this.tunnel == undefined )
			return false;

		return true;
	}
	checkStorageForTunnelParameters()
	{	
		this.tunnel = this.tunnel? this.tunnel : localStorage.getItem('tunnel');
    	this.username = this.username? this.username : localStorage.getItem('username');
    	this.password = this.password? this.password: localStorage.getItem('password');
	}

	// once the tunnel parameters have been used, they should be reset
	resetTunnelParameters()
	{
		this.tunnel = undefined;
		this.username = undefined;
		this.password = undefined;
		localStorage.removeItem('tunnel');
		localStorage.removeItem('username');
		localStorage.removeItem('password');
	}

	// processes the message from the gwp portal
	// If the tunnel was used, it will be reset
	// the state of the gwp will be emitted
	handleMessageFromChild( event: any )
	{
		if (event && event.data && event.data.type && event.data.type === 'CL_DONEINTEGRATION') {
			console.log('iframe child said its done integration', event);
			if ( event.data.tunnelUsed !== undefined && event.data.tunnelUsed )
			{
				this.resetTunnelParameters();
			}
			this.gwpInprogessState = false;
			this.gwpInprogessStateChange.next({
				inProgressState: this.gwpInprogessState, 
				redirectDestination: event.data.redirectDestination, 
				fromInsideIframe: event.data.fromInsideIframe
			});
			var iframeSpa = (<HTMLIFrameElement>document.getElementById("iframeSpa"));
			if (iframeSpa) {
				iframeSpa.contentWindow.postMessage("CL_UNLOAD", "*");
			}
			setTimeout(() => {
				this.gwpDoneChildMessage.next(true);
			});
		}
	}

	setGWPInprogressState( state: boolean )
	{
		console.log("Setting the state to ", state );
		this.gwpInprogessState = state;
		this.gwpInprogessStateChange.next({inProgressState: this.gwpInprogessState});
	}

	isGWPInprogress()
	{
		return this.gwpInprogessState;
	}

	isGatewayConnected(tags): boolean {
		return tags && tags['on-board-progress'] &&
			tags['on-board-progress'].step >= 2 &&
			tags['gateway-connection'] &&
			tags['gateway-connection'].connected === true;
	}

	getTunnelParametersAsQueryParamString()
	{
		if ( this.tunnel == undefined )
			return null;
		return 'tunnel=' + this.tunnel + '&username=' + this.username + '&password=' + this.password;
	}

	public sendResetRequest(accountId: string, siteId: string): Promise<any> {
		return this.cloudTunnel.deleteSystem({ accountId, siteId});
	}	

	public getNetworkSettings(accountId: string, siteId: string): Promise<any> {
		return this.cloudTunnel.getNetworkSettings({ accountId, siteId });
	}

	// Couldn't find any usage for this method anywhere in the app. I am leaving it for now but someone  
	// who is more familar of gateway integration code than me can have a look to remove it in future.
	public getPlatform(accountId: string, siteId: string, options?: any): Promise<any> {
		return this.cloudTunnel.getPlatform({ accountId, siteId, options });
	}

	
	setGWPFlag(access: boolean) {
		this.gwpAccess = access;
		this.gwp.next(access);
	}
	
	getGWPFlag() {
		return this.gwpAccessFlagChanged;
	}

	hasGWPAccess() {
		return this.gwpAccess;
	}

	/*
	gwpAccess is based on the policies
	If policies are there, also need to check if account is a prime account.
	Prime accounts are not allowed to onboard anymore. But if they already have, then ok
	*/
	async doesAccountHaveGWPAccess(accountId): Promise<Boolean> {
		if(!this.gwpAccess){
			return Promise.resolve(false);
		}

		return this.miaccessService.checkPAOnboarding(accountId).then( flag => {
			return flag;
		})
		.catch( err => {
			console.error("Could not determine the Gateway access", err);
			return false;
		});
	}

	resetEmailParams(){
		this.emailsAllowed = false;
		this.emailApplicationName = null;
		this.emailApplicationNameLong = null;
	}

	/*
	Determines if sending Gateway Emails makes sense for the account.
	1. Does account have GWP access (consider policies and prime)
	2. Do product tags include Gateway (integration is enabled)
	3. Have they onboarded far enough so sending emails makes sense (past connect step)
	4. Do we know which application to send emails for - MOMA or OfficeLink
	If all of these checks pass, it's ok to send emails
	*/
	async shouldSendingGWPEmailsBeEnabled(accountId: string): Promise<boolean>
	{
		this.resetEmailParams();
		try {
			let tags = await this.gatewayStatusService.getTagStatus(accountId);
			const products: Array<String> = tags && tags.products? tags.products : null;

			if(products && products.length > 0){
				let isCapEnabled = false;
				let mivbEmailsAllowed = false;

				//only bother checking CAP if MiVB integration is added
				if(products.includes('MiVB')){
					isCapEnabled = await this.capService.isCAPFeatureEnabled();

					if(isCapEnabled){
						//If CAP is enabled - Administration is toggled on for the MiVoice Business account integration
						//If CAP is enabled - we are reasonably sure we're dealing with an MiVB situation - so it's worth checking the network elements via the mivb-micro
						//It should be true that an mivb is detected in the network elements - since that's a condition to turn CAP on
						//But if pbxs are decommissioned, I don't see any logic to turn OFF CAP - so we still need to confirm an MiVB is present
						//If there is an mivb in the network elements then we should allow for mivb emails unless in a weird state with conflicting pbx tags
						mivbEmailsAllowed = await this.mivbAccountSettingsService.isMiVBInNetworkElements();
					}
				}

				//Regardless of if CAP is enabled, we still check if anything has been onboarded in GWP
				//Network call to check for mivb in netowrk elements may have failed.
				//This is also done to confirm we're not in a weird state where there are tags that suggest different types of pbxs
				
				let accessFlag = await this.doesAccountHaveGWPAccess(accountId);
				let onboardingInGWPDone = false;
				let pbxTypesFromTags;

				if(accessFlag){
					let gwpIntegrationON = products.includes('Gateway') ? true: false;
					if(gwpIntegrationON){
						onboardingInGWPDone = Util.isOnboardingDoneInGWP(tags);
						pbxTypesFromTags = Util.getPbxTypesFromTags(tags);

						if(pbxTypesFromTags && pbxTypesFromTags.length > 1){
							console.log("Conflicting pbx information for sending deployment emails");
							this.resetEmailParams();
							return false;
						}
						else if(onboardingInGWPDone && (!pbxTypesFromTags || pbxTypesFromTags.length <1)){
							console.log("Missing pbx information for sending deployment emails");
							this.resetEmailParams();
							return false;
						}
					}
				}
				
				/* 
				Only sending emails if:
				  - mivb found in network elements (and if tags have a pbx that it can only be MiVoice Business)
				  - no mivb in network elements, then onboarding must be complete and pbx type can not be MiVoice Business
				*/
				if(mivbEmailsAllowed || onboardingInGWPDone){
					if(mivbEmailsAllowed){ //cap enabled for this to be true
						if(pbxTypesFromTags && pbxTypesFromTags.length === 1 && pbxTypesFromTags[0] != "MiVoice Business"){
							//something is wrong. There must be product tags coming from the GWP that are related to a non-mivb PBX
							console.log("Conflicting information for sending deployment emails");
							this.resetEmailParams();
							return false;
						}
						//directly set it since 'MiVoice Business' may not be in the product tags if CLD setup
						this.emailApplicationName = "MiVoice Business";
					}
					else if(pbxTypesFromTags && pbxTypesFromTags.length === 1 && pbxTypesFromTags[0] === "MiVoice Business"){ //onboardingInGWPDone
						//Not a CLD setup.
						//Classic setup of onboarding via Gateway Portal. But missing mivb in network elements
						//unexpected case
						console.log("Missing CAP or could not detect mivb for sending deployment emails");
						this.resetEmailParams();
						return false;
					}
					else{ //onboardingInGWPDone
						//a valid pbx is onboarded via GWP and it's not a MiVoice Business
						this.emailApplicationName = this.getEmailApplicationNameFromTags(products); //just from GWP related tags
					}
					
					if(this.emailApplicationName){
						this.emailApplicationNameLong = this.getEmailApplicationLongName(this.emailApplicationName);
						//If we get to this point We know which emails to send
						console.log("Deployment emails allowed for : ", this.emailApplicationName);
						this.emailsAllowed = true;
						return true;
					} else{
						//We don't know which emails to send
						console.log("Unknown application for gateway deployment emails");
					}
				}
			}
			this.resetEmailParams();
			return false;	
		} catch (error) {
			this.resetEmailParams();
			return false;
		}
	}

	/*
	Only return an application name if we know it has a specific email.
	To better understand product tags, look up pbxProducts in constants.ts of Gateway Portal
	Keep this method just for pbx tags that the Gateway Portal sets
	*/
	getEmailApplicationNameFromTags(productTags: Array<String>){
		if(productTags){
			if(productTags.includes("Mitel OfficeLink")){
				return "OfficeLink"; //MIVO250
			} else if(productTags.includes("MiVoice Business")){
				return "MiVoice Business" //MIVB
			} else if(productTags.includes("Mitel Office")){
				return "Mitel Office" //MIVO400, MIVC
			}
		}
		return null;
	}

	getEmailApplicationLongName(applicationName: string){

		if(applicationName === "OfficeLink"){
			return "Mitel OfficeLink";
		} else if(applicationName === "MiVoice Business"){
			return "MiVoice Business";
		} else if(applicationName === "Mitel Office"){
			return "Mitel Office Mobile Application";
		} else {
			return null;
		}
	}

}
